/**
 * Creator lifecycle progression from visitor → scaled creator.
 * Structural model only — no runtime behavior or persistence.
 */
export enum CreatorLifecycleState {
  VISITOR = 'visitor',
  USER = 'user',
  ASPIRING_CREATOR = 'aspiring_creator',
  ACTIVE_CREATOR = 'active_creator',
  MONETIZING_CREATOR = 'monetizing_creator',
  SCALED_CREATOR = 'scaled_creator',
}

/** Canonical progression order for the Loamify creator journey. */
export const CREATOR_LIFECYCLE_PROGRESSION: readonly CreatorLifecycleState[] = [
  CreatorLifecycleState.VISITOR,
  CreatorLifecycleState.USER,
  CreatorLifecycleState.ASPIRING_CREATOR,
  CreatorLifecycleState.ACTIVE_CREATOR,
  CreatorLifecycleState.MONETIZING_CREATOR,
  CreatorLifecycleState.SCALED_CREATOR,
];

/** Allowed forward transitions (structural definition, not enforced at runtime yet). */
export const CREATOR_LIFECYCLE_TRANSITIONS: Readonly<
  Record<CreatorLifecycleState, CreatorLifecycleState | null>
> = {
  [CreatorLifecycleState.VISITOR]: CreatorLifecycleState.USER,
  [CreatorLifecycleState.USER]: CreatorLifecycleState.ASPIRING_CREATOR,
  [CreatorLifecycleState.ASPIRING_CREATOR]: CreatorLifecycleState.ACTIVE_CREATOR,
  [CreatorLifecycleState.ACTIVE_CREATOR]: CreatorLifecycleState.MONETIZING_CREATOR,
  [CreatorLifecycleState.MONETIZING_CREATOR]: CreatorLifecycleState.SCALED_CREATOR,
  [CreatorLifecycleState.SCALED_CREATOR]: null,
};

/** Returns the next lifecycle state in the canonical progression, or null at terminal state. */
export function getNextLifecycleState(
  current: CreatorLifecycleState,
): CreatorLifecycleState | null {
  return CREATOR_LIFECYCLE_TRANSITIONS[current] ?? null;
}

/**
 * Evolves lifecycle state structurally.
 * When `target` is omitted, advances one step forward.
 * When `target` is provided, returns it only if it is a valid next state.
 */
export function evolveCreatorLifecycleState(
  current: CreatorLifecycleState,
  target?: CreatorLifecycleState,
): CreatorLifecycleState {
  const expectedNext = getNextLifecycleState(current);

  if (target === undefined) {
    return expectedNext ?? current;
  }

  if (target === expectedNext) {
    return target;
  }

  return current;
}
